import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { FaSlidersH } from "react-icons/fa";
import PageTemplate from "../../../../components/PageTemplate";
import Spinner from "../../../../components/Spinner";
import SlidePageTransition from "../../../../components/transitions/SlidePageTransition";
import { useCan } from "../../../../authz/useCan";
import { mapHttpErrorToMessage } from "../../../../utils/mapHttpErrorToMessage";
import { MutationProcessingList } from "./components/MutationProcessingList";
import { useMutationProcessingSettings } from "./hooks/useMutationProcessingSettings";
import { useUpdateMutationProcessingSetting } from "./hooks/useUpdateMutationProcessingSetting";

const PERMISSION_RESOURCE = "administration.mutation-processing";

const ldsAnimationStyles = `
  @keyframes lds-page-enter {
    from {
      opacity: 0;
      transform: translateY(14px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes lds-section-enter {
    from {
      opacity: 0;
      transform: translateY(18px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .lds-page-enter {
    animation: lds-page-enter 420ms cubic-bezier(0.22, 1, 0.36, 1) both;
  }

  .lds-section-enter {
    animation: lds-section-enter 480ms cubic-bezier(0.22, 1, 0.36, 1) both;
  }

  @media (prefers-reduced-motion: reduce) {
    .lds-page-enter,
    .lds-section-enter {
      animation: none;
    }
  }
`;

const emitToast = (detail: { level: "success" | "error"; title: string; message: string }) => {
  if (typeof window !== "undefined" && typeof window.dispatchEvent === "function") {
    window.dispatchEvent(new CustomEvent("lumen:toast", { detail }));
  }
};

const messageFromError = (error: unknown, fallback: string) => {
  const candidate = error as { response?: { data?: { message?: string } }; message?: string };
  return candidate?.response?.data?.message || candidate?.message || fallback;
};

const MutationProcessingPage = () => {
  const { t } = useTranslation();
  const canRead = useCan(PERMISSION_RESOURCE, "read");
  const canUpdate = useCan(PERMISSION_RESOURCE, "update");
  const { settings, setSettings, loading, error, reload } = useMutationProcessingSettings(canRead);

  const updateOptions = useMemo(
    () => ({
      onSuccess: () => {
        emitToast({
          level: "success",
          title: t("admin.mutation_processing.messages.save_success", { defaultValue: "Policy saved" }),
          message: t("admin.mutation_processing.messages.save_success_detail", {
            defaultValue: "The mutation processing setting has been updated.",
          }),
        });
      },
      onError: (updateError: unknown) => {
        emitToast({
          level: "error",
          title: t("admin.mutation_processing.messages.save_error", { defaultValue: "Could not save policy" }),
          message: messageFromError(
            updateError,
            t("admin.mutation_processing.messages.save_error_detail", {
              defaultValue: "The mutation processing setting could not be saved.",
            })
          ),
        });
      },
    }),
    [t]
  );

  const { savingType, updateSetting } = useUpdateMutationProcessingSetting(setSettings, updateOptions);

  const pageTitle = t("admin.mutation_processing.title", { defaultValue: "Mutation processing" });

  if (loading) {
    return (
      <PageTemplate title={null} wrapperClassName="max-w-none">
        <style>{ldsAnimationStyles}</style>
        <div className="lds-page-enter mx-auto max-w-7xl rounded-[32px] border border-slate-200/80 bg-white/95 p-5 shadow-[0_26px_70px_rgba(15,23,42,0.12)] dark:border-white/10 dark:bg-slate-950/85 sm:p-6">
          <SlidePageTransition className="space-y-5">
            <header className="lds-section-enter relative overflow-hidden rounded-[28px] border border-indigo-100 bg-gradient-to-r from-indigo-50 via-white to-sky-50 px-5 py-6 shadow-sm dark:border-white/10 dark:from-indigo-950/35 dark:via-slate-950 dark:to-sky-950/30 sm:px-7 sm:py-7">
              <div className="relative z-10 flex flex-col gap-5 sm:flex-row sm:items-start">
                <div className="lumen-glow-sheen flex h-20 w-20 shrink-0 items-center justify-center rounded-full border-4 border-white bg-gradient-to-br from-violet-200 to-sky-50 text-3xl text-violet-700 shadow-lg dark:border-slate-900 dark:from-violet-800 dark:to-sky-950 dark:text-violet-100 sm:h-24 sm:w-24">
                  <FaSlidersH aria-hidden="true" />
                </div>
                <div className="min-w-0">
                  <h1 className="text-3xl font-black text-slate-950 dark:text-white">{pageTitle}</h1>
                  <p className="mt-1 max-w-3xl text-sm leading-6 text-slate-600 dark:text-slate-300">
                    {t("admin.mutation_processing.heading", { defaultValue: "Processing policy per mutation type" })}
                  </p>
                </div>
              </div>
            </header>
            <div className="lds-section-enter lumen-plain-card rounded-2xl p-8">
              <div className="flex items-center justify-center gap-3 text-sm font-semibold text-slate-600 dark:text-slate-300">
                <Spinner />
                <span>{t("admin.mutation_processing.loading", { defaultValue: "Loading mutation processing settings..." })}</span>
              </div>
            </div>
          </SlidePageTransition>
        </div>
      </PageTemplate>
    );
  }

  const loadError = error
    ? messageFromError(
        error,
        mapHttpErrorToMessage(error, t).message ||
      t("admin.mutation_processing.messages.load_error", {
        defaultValue: "Mutation processing settings could not be loaded.",
      })
      )
    : null;

  return (
    <PageTemplate title={null} wrapperClassName="max-w-none">
      <style>{ldsAnimationStyles}</style>
      <div className="lds-page-enter mx-auto max-w-7xl rounded-[32px] border border-slate-200/80 bg-white/95 p-5 shadow-[0_26px_70px_rgba(15,23,42,0.12)] dark:border-white/10 dark:bg-slate-950/85 sm:p-6">
        <SlidePageTransition className="space-y-5">
          <header className="lds-section-enter relative overflow-hidden rounded-[28px] border border-indigo-100 bg-gradient-to-r from-indigo-50 via-white to-sky-50 px-5 py-6 shadow-sm dark:border-white/10 dark:from-indigo-950/35 dark:via-slate-950 dark:to-sky-950/30 sm:px-7 sm:py-7">
            <div className="relative z-10 flex flex-col gap-5 sm:flex-row sm:items-start">
              <div className="lumen-glow-sheen flex h-20 w-20 shrink-0 items-center justify-center rounded-full border-4 border-white bg-gradient-to-br from-violet-200 to-sky-50 text-3xl text-violet-700 shadow-lg dark:border-slate-900 dark:from-violet-800 dark:to-sky-950 dark:text-violet-100 sm:h-24 sm:w-24">
                <FaSlidersH aria-hidden="true" />
              </div>
              <div className="min-w-0">
                <h1 className="text-3xl font-black text-slate-950 dark:text-white">{pageTitle}</h1>
                <h2 className="mt-2 text-lg font-bold text-slate-800 dark:text-slate-100">
                  {t("admin.mutation_processing.heading", { defaultValue: "Processing policy per mutation type" })}
                </h2>
                <p className="mt-1 max-w-3xl text-sm leading-6 text-slate-600 dark:text-slate-300">
                  {canUpdate
                    ? t("admin.mutation_processing.description", {
                        defaultValue: "Choose which parent changes are available and how the backend processes them.",
                      })
                    : t("admin.mutation_processing.readonly", {
                        defaultValue: "You can view settings, but you cannot change them.",
                      })}
                </p>
              </div>
            </div>
          </header>

          {loadError && (
            <div className="lds-section-enter lumen-plain-card rounded-2xl border-red-200 bg-red-50 p-5 text-sm font-semibold text-red-800 dark:border-red-500/40 dark:bg-red-900/25 dark:text-red-100">
              <div>{loadError}</div>
              <button
                type="button"
                onClick={() => void reload()}
                className="mt-3 rounded-xl border border-red-200 bg-white px-3 py-2 text-xs font-bold text-red-700 transition hover:bg-red-50 dark:border-red-400/30 dark:bg-red-950/40 dark:text-red-100 dark:hover:bg-red-900/40"
              >
                {t("error.error_reload_button", { defaultValue: "Try again" })}
              </button>
            </div>
          )}

          {!loadError && (
            <div className="lds-section-enter">
              <MutationProcessingList
                settings={settings}
                canUpdate={canUpdate}
                savingType={savingType}
                onUpdate={(setting, payload) => void updateSetting(setting, payload)}
              />
            </div>
          )}
        </SlidePageTransition>
      </div>
    </PageTemplate>
  );
};

export default MutationProcessingPage;
